import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api.js';
import { dateTime } from '../lib/format.js';
import type { Me } from '../lib/session.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import '../screens-layers.css';

interface LayerCard {
  id: string;
  wave: number;
  label: string;
  word: string;
  summary: string;
  offEffect: string;
  dependsOn: string[];
  reserved: boolean;
  state: 'not_allowed' | 'allowed' | 'enabled';
  active: boolean;
  reason: string;
  missing: string[];
  dependents: string[];
  scope: { routes: number; jobs: string[]; screens: string[]; tools: number; codes: string[] };
}
interface Overview {
  mode: 'legacy_all' | 'explicit';
  environment: string | null;
  recommendation?: string;
  layers: LayerCard[];
}
interface Check {
  key: string;
  label: string;
  status: 'ok' | 'warn' | 'block';
  detail: string;
}
interface Readiness {
  id: string;
  ready: boolean;
  checks: Check[];
}
interface HistoryItem {
  at: string;
  layer: string | null;
  action: string;
  by: string | null;
  environment: string | null;
  forced: boolean;
  reason: string | null;
}

const CHECK_TONE: Record<Check['status'], Tone> = { ok: 'ok', warn: 'warn', block: 'bad' };
const CHECK_TEXT: Record<Check['status'], string> = {
  ok: 'v pořádku',
  warn: 'varování',
  block: 'blokuje',
};

function stateBadge(l: LayerCard, legacy: boolean): JSX.Element {
  if (l.reserved) return <StatusBadge tone="neu">Rezervováno</StatusBadge>;
  if (legacy && l.state === 'enabled') {
    return (
      <StatusBadge tone="acc" icon="check">
        Zapnuto (režim kompatibility)
      </StatusBadge>
    );
  }
  if (l.state === 'not_allowed') return <StatusBadge tone="neu">Nepovoleno v nasazení</StatusBadge>;
  if (l.state === 'allowed') return <StatusBadge tone="warn">Povoleno, vypnuto</StatusBadge>;
  return l.active ? (
    <StatusBadge tone="ok">Zapnuto</StatusBadge>
  ) : (
    <StatusBadge tone="bad">Zapnuto, ale neběží</StatusBadge>
  );
}

const ACTION_TEXT: Record<string, string> = {
  'layer.enable': 'zapnuto',
  'layer.disable': 'vypnuto',
};

/** Nastavení → Vlny nasazení (L4): stav vln, kontrolní seznam připravenosti, zapnutí a vypnutí, historie. */
export function LayersTab({ me }: { me: Me }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery<Overview>({ queryKey: ['layers'], queryFn: () => api.get('/api/layers') });
  const history = useQuery<{ items: HistoryItem[] }>({
    queryKey: ['layers', 'history'],
    queryFn: () => api.get('/api/layers/history'),
  });
  const [asking, setAsking] = useState<{ id: string; action: 'enable' | 'disable' } | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const readiness = useQuery<Readiness>({
    queryKey: ['layers', 'readiness', asking?.id ?? checking],
    queryFn: () => api.get(`/api/layers/${asking?.id ?? checking}/readiness`),
    enabled: Boolean(asking?.id ?? checking),
  });

  const close = (): void => {
    setAsking(null);
    setForce(false);
    setReason('');
  };
  const change = useMutation({
    mutationFn: (v: { id: string; action: 'enable' | 'disable'; word?: string }) =>
      api.post(`/api/layers/${v.id}/${v.action}`, {
        confirmation: v.word,
        ...(force ? { force: true, reason } : {}),
      }),
    onSuccess: async () => {
      setError(null);
      close();
      await qc.invalidateQueries({ queryKey: ['layers'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'readiness_failed') {
        setForce(true);
        setError(null);
        return;
      }
      setError(err instanceof Error ? err.message : 'Změna se nepovedla.');
    },
  });

  if (q.isLoading) return <LoadingState what="vlny nasazení" />;
  if (q.isError || !q.data) {
    return (
      <ErrorState error={q.error ?? new Error('Nelze načíst')} onRetry={() => void q.refetch()} />
    );
  }
  const data = q.data;
  const legacy = data.mode === 'legacy_all';
  const byId = new Map(data.layers.map((l) => [l.id, l]));
  const asked = asking ? byId.get(asking.id) : undefined;
  const production = data.environment === 'production';
  const blockers = (readiness.data?.checks ?? []).filter((c) => c.status === 'block');

  return (
    <>
      <p className="lede">
        Hub se nasazuje po vlnách. Nový kód leží „potmě“, dokud vlnu někdo nezapne: nasazení samo
        žádnou vlnu nezapne. Zapnout lze jen vlnu, kterou nasazení povoluje (proměnná{' '}
        <code>HUB_LAYERS_ALLOWED</code>), a jen po zapnutí vln, na kterých závisí. Vypnutá vlna nic
        nepřijímá, neplánuje ani neodesílá; data zůstávají. Zapínat a vypínat smí jen administrátor.
      </p>
      {legacy ? (
        <div className="note" data-testid="layers-legacy">
          {data.recommendation}
        </div>
      ) : null}
      {error ? <ErrorState error={new Error(error)} title="Změna se nepovedla" /> : null}

      <div className="layers-list">
        {data.layers.map((l) => {
          const canEnable = !legacy && !l.reserved && l.state === 'allowed' && l.id !== 'base';
          const canDisable = !legacy && l.state === 'enabled' && l.id !== 'base';
          const open = checking === l.id;
          return (
            <section
              key={l.id}
              className={`layercard${l.active ? ' on' : ''}`}
              aria-label={`Vlna ${l.wave}: ${l.label}`}
              data-testid={`layer-${l.id}`}
            >
              <header>
                <span className="layerwave">Vlna {l.wave}</span>
                <h3>{l.label}</h3>
                {stateBadge(l, legacy)}
              </header>
              <p>{l.summary}</p>
              <dl className="kv">
                <dt>Závisí na</dt>
                <dd>
                  {l.dependsOn.length === 0
                    ? 'nezávisí na ničem'
                    : l.dependsOn.map((d) => byId.get(d)?.label ?? d).join(', ')}
                </dd>
                <dt>Když se vypne</dt>
                <dd>{l.offEffect}</dd>
                {l.state === 'enabled' && !l.active && l.missing.length > 0 ? (
                  <>
                    <dt>Neběží, protože</dt>
                    <dd>
                      není zapnuto: {l.missing.map((d) => byId.get(d)?.label ?? d).join(', ')}
                    </dd>
                  </>
                ) : null}
              </dl>
              {!legacy && !l.reserved && l.id !== 'base' ? (
                <div className="layeraction">
                  <button
                    type="button"
                    className="btn"
                    aria-expanded={open}
                    onClick={() => setChecking(open ? null : l.id)}
                  >
                    {open ? 'Skrýt kontroly připravenosti' : 'Zkontrolovat připravenost'}
                  </button>
                  {canEnable ? (
                    <button
                      type="button"
                      className="btn pri"
                      onClick={() => {
                        setError(null);
                        setAsking({ id: l.id, action: 'enable' });
                      }}
                    >
                      Zapnout
                    </button>
                  ) : null}
                  {canDisable ? (
                    <button
                      type="button"
                      className="btn dng"
                      onClick={() => {
                        setError(null);
                        setAsking({ id: l.id, action: 'disable' });
                      }}
                    >
                      Vypnout
                    </button>
                  ) : null}
                </div>
              ) : null}
              {open ? <Checklist query={readiness} /> : null}
            </section>
          );
        })}
      </div>

      <Panel
        title="Historie změn"
        subtitle={`Kdo, kdy a kterou vlnu zapnul nebo vypnul v tomto prostředí (${me.environment?.name ?? 'bez přepínání'}).`}
      >
        {history.isLoading ? <LoadingState what="historii" /> : null}
        {history.data && history.data.items.length === 0 ? (
          <EmptyState title="Zatím žádná změna">
            Vlny se v tomto prostředí ještě nezapínaly ani nevypínaly.
          </EmptyState>
        ) : null}
        {history.data && history.data.items.length > 0 ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Kdy</th>
                  <th>Vlna</th>
                  <th>Změna</th>
                  <th>Kdo</th>
                  <th>Poznámka</th>
                </tr>
              </thead>
              <tbody>
                {history.data.items.map((h) => (
                  <tr key={`${h.at}-${h.layer}-${h.action}`}>
                    <td>{dateTime(h.at)}</td>
                    <td>{byId.get(h.layer ?? '')?.label ?? h.layer}</td>
                    <td>{ACTION_TEXT[h.action] ?? h.action}</td>
                    <td>{h.by ?? 'neznámý'}</td>
                    <td>{h.forced ? `Obejita kontrola: ${h.reason ?? ''}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>

      <ConfirmDialog
        open={asking !== null && asked !== undefined}
        title={
          asking?.action === 'enable'
            ? `Zapnout vlnu ${asked?.wave}: ${asked?.label}?`
            : `Vypnout vlnu ${asked?.wave}: ${asked?.label}?`
        }
        confirmLabel={asking?.action === 'enable' ? 'Zapnout vlnu' : 'Vypnout vlnu'}
        danger={asking?.action === 'disable' || production}
        requireWord={asked?.word}
        busy={change.isPending || (force && reason.trim().length < 3)}
        onCancel={close}
        onConfirm={(word) => asking && change.mutate({ ...asking, word })}
      >
        {asked && asking?.action === 'enable' ? (
          <>
            <p>{asked.summary}</p>
            <p>
              Zapnutí platí jen pro toto prostředí
              {data.environment ? ` (${data.environment})` : ''}. Zpráva se začne odesílat, jen
              pokud je zapnutý i režim odesílání a přepínač kódu.
            </p>
            {readiness.data ? <Checklist query={readiness} /> : null}
            {production && blockers.length > 0 ? (
              <>
                <p className="note">
                  V Produkci blokující kontroly zapnutí zastaví. Obejít je smí administrátor s
                  důvodem; zapíše se do auditu.
                </p>
                {force ? (
                  <Field label="Důvod obejití kontrol (povinný)">
                    <input
                      type="text"
                      value={reason}
                      maxLength={500}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </Field>
                ) : null}
              </>
            ) : null}
          </>
        ) : asked ? (
          <>
            <p>{asked.offEffect}</p>
            <p>
              Data zůstanou, vlnu lze znovu zapnout. Zadržené zprávy odejdou, až se vlna zapne a
              dokud platí.
            </p>
          </>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

function Checklist({
  query,
}: {
  query: { isLoading: boolean; isError: boolean; data?: Readiness; error: unknown };
}): JSX.Element {
  if (query.isLoading) return <LoadingState what="kontroly připravenosti" />;
  if (query.isError || !query.data) {
    return <ErrorState error={query.error instanceof Error ? query.error : new Error('Chyba')} />;
  }
  return (
    <ul className="layerchecks" aria-label="Kontroly připravenosti">
      {query.data.checks.map((c) => (
        <li key={c.key}>
          <StatusBadge tone={CHECK_TONE[c.status]}>{CHECK_TEXT[c.status]}</StatusBadge>
          <span>
            <b>{c.label}.</b> {c.detail}
          </span>
        </li>
      ))}
    </ul>
  );
}
